// API client del plan de cuentas (Sprint 1 2026-05-16).
//
// Endpoints:
//   GET    /api/accounts/:accountId/chart
//   POST   /api/accounts/:accountId/chart/accounts
//   PATCH  /api/accounts/:accountId/chart/accounts/:chartAccountId
//   DELETE /api/accounts/:accountId/chart/accounts/:chartAccountId

import { api } from "@/common/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mappingsKey } from "./journalApi";

export type AccountType = "ASSET" | "LIABILITY" | "EQUITY" | "INCOME" | "EXPENSE" | "COST";

export type ChartNode = {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  parentId: string | null;
  isSystem: boolean;
  isContra: boolean;
  isActive: boolean;
  semanticKey: string | null;
  // Sprint 2 (2026-05-17): true para cuentas con auxiliar contable por animal.
  // La UI lo usa para mostrar el botón "Ver auxiliar" en el plan de cuentas.
  hasAuxiliary: boolean;
};

export type GetChartResponse = { rows: ChartNode[] };

export const chartKey = (accountId: string) => ["chart", accountId] as const;

export function useChart(accountId: string) {
  return useQuery({
    queryKey: chartKey(accountId),
    queryFn: () => api.get<GetChartResponse>(`/accounts/${accountId}/chart`),
    enabled: Boolean(accountId),
    staleTime: 60_000,
  });
}

export function useCreateChartAccount(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { parentId: string; code: string; name: string }) =>
      api.post<ChartNode>(`/accounts/${accountId}/chart/accounts`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chartKey(accountId) });
      void qc.invalidateQueries({ queryKey: mappingsKey(accountId) });
    },
  });
}

export function usePatchChartAccount(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { chartAccountId: string; name?: string; isActive?: boolean }) =>
      api.patch<ChartNode>(`/accounts/${accountId}/chart/accounts/${input.chartAccountId}`, {
        ...(input.name !== undefined ? { name: input.name } : {}),
        ...(input.isActive !== undefined ? { isActive: input.isActive } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chartKey(accountId) });
      void qc.invalidateQueries({ queryKey: mappingsKey(accountId) });
    },
  });
}

export function useDeleteChartAccount(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chartAccountId: string) =>
      api.delete<void>(`/accounts/${accountId}/chart/accounts/${chartAccountId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chartKey(accountId) });
      void qc.invalidateQueries({ queryKey: mappingsKey(accountId) });
    },
  });
}

// Helper: ordenar planamente con jerarquía indentada (para visualización).
// Estable y determinista — usa el código del árbol (ordenable lexicográfico).
export type ChartNodeWithDepth = ChartNode & { depth: number };

export function flattenChartByHierarchy(rows: ChartNode[]): ChartNodeWithDepth[] {
  const byParent = new Map<string | null, ChartNode[]>();
  for (const r of rows) {
    const arr = byParent.get(r.parentId) ?? [];
    arr.push(r);
    byParent.set(r.parentId, arr);
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));
  }
  const out: ChartNodeWithDepth[] = [];
  function walk(parentId: string | null, depth: number) {
    const children = byParent.get(parentId) ?? [];
    for (const c of children) {
      out.push({ ...c, depth });
      walk(c.id, depth + 1);
    }
  }
  walk(null, 0);
  return out;
}
